import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { VipLimitProvider } from './contexts/VipLimitContext';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';
import ProtectedRoute from './components/ProtectedRoute';
import AuthPage from './pages/auth/AuthPage';
import Home from './pages/home/Home';
import Discover from './pages/home/Discover';
import StudyMenuPage from './pages/study/StudyMenuPage';
import StudyBooksPage from './pages/study/StudyBooksPage';
import StudyWordsSwitcher from './pages/study/StudyWordsSwitcher';
import StudySentencesSwitcher from './pages/study/StudySentencesSwitcher';
import StudyNotesSwitcher from './pages/study/StudyNotesSwitcher';
import Profile from './pages/profile/Profile';
import ProfileAccount from './pages/profile/ProfileAccount';
import ProfileAvatar from './pages/profile/ProfileAvatar';
import HelpCenter from './pages/info/HelpCenter';
import Feedback from './pages/info/Feedback';
import BookDetail from './pages/book/BookDetail';
import ChapterReader from './pages/reader/ChapterReader';
import ChapterLearn from './pages/reader/ChapterLearn';
import WordStudy from './pages/study/WordStudy';
import SentenceStudy from './pages/study/SentenceStudy';
import NoteStudy from './pages/study/NoteStudy';
import AdminApp from './admin/AdminApp';
import ReadingHistoryPage from './pages/profile/ReadingHistoryPage';
import About from './pages/info/About';
import CategoryBooks from './pages/home/CategoryBooks';
import VipRenewal from './pages/vip/VipRenewal';
import Settings from './pages/profile/Settings';
import TermsAndPrivacy from './pages/info/TermsAndPrivacy';
import VipLimitPage from './pages/vip/VipLimitPage';
import NovelVoting from './pages/home/NovelVoting';
import styles from './App.module.css';

// Wrapper：使用 location.key 强制 BookDetail 重新挂载
const BookDetailWrapper = () => {
  const location = useLocation();
  return <BookDetail key={location.key} />;
};

// 主应用组件
const AppContent = () => {
  const { isAuthenticated, loading } = useAuth();
  const debugLocation = useLocation();
  console.log('[AppContent] render, path:', debugLocation.pathname, 'search:', debugLocation.search, 'isAuth:', isAuthenticated, 'loading:', loading);

  if (loading) {
    return (
      <div className={styles.appLoading}>
        <LoadingSpinner size="large" text="加载中..." />
      </div>
    );
  }

  // 如果未认证，显示登录/注册页面
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/terms" element={<TermsAndPrivacy />} />
        <Route path="/vip-limit" element={<VipLimitPage />} />
        <Route path="/register" element={<AuthPage initialView="register" />} />
        <Route path="/login" element={<AuthPage initialView="login" />} />
        <Route path="*" element={<AuthPage initialView="login" />} />
      </Routes>
    );
  }

  // 已认证：Layout 自动根据路由决定是否显示导航栏
  return (
    <Layout>
      <Routes>
        {/* 管理员路由 */}
        <Route path="/admin/*" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminApp />
          </ProtectedRoute>
        } />
        
        {/* VIP 限额提示页 */}
        <Route path="/vip-limit" element={<VipLimitPage />} />

        {/* 专注学习路由（Layout 会自动隐藏导航栏） */}
        <Route path="/wordstudy/:bookId/:chapterId" element={<WordStudy />} />
        <Route path="/learn/word" element={<WordStudy />} />
        <Route path="/learn/words" element={<WordStudy />} />
        <Route path="/learn/:bookId" element={<WordStudy />} />
        <Route path="/learn/sentences" element={<SentenceStudy />} />
        <Route path="/learn/sentences/:bookId?/:chapterId?" element={<SentenceStudy />} />
        <Route path="/learn/notes" element={<NoteStudy />} />
        <Route path="/learn/notes/:bookId" element={<NoteStudy />} />
        
        {/* 普通页面路由（Layout 会自动显示导航栏） */}
        <Route path="/" element={<Home />} />
        <Route path="/discover" element={<Discover />} />
        <Route path="/categories/:categoryId" element={<CategoryBooks />} />
        <Route path="/study" element={<StudyMenuPage />} />
        <Route path="/study/words" element={<StudyWordsSwitcher />} />
        <Route path="/study/sentences" element={<StudySentencesSwitcher />} />
        <Route path="/study/notes" element={<StudyNotesSwitcher />} />
        <Route path="/study/books" element={<StudyBooksPage />} />
        <Route path="/study/study/:bookId" element={<WordStudy />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/help" element={<HelpCenter />} />
        <Route path="/profile/history" element={<ReadingHistoryPage />} />
        <Route path="/profile/account" element={<ProfileAccount />} />
        <Route path="/profile/account/avatar" element={<ProfileAvatar />} />
        <Route path="/voting" element={<NovelVoting />} />
        <Route path="/vip/renewal" element={<VipRenewal />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/about" element={<About />} />
        <Route path="/terms" element={<TermsAndPrivacy />} />
        <Route path="/book" element={<BookDetailWrapper />} />
        <Route path="/read" element={<ChapterReader />} />
        <Route path="/chapterlearn" element={<ChapterLearn />} />

        {/* 已登录用户访问注册/登录页面时也能正常显示 */}
        <Route path="/register" element={<AuthPage initialView="register" />} />
        <Route path="/login" element={<AuthPage initialView="login" />} />
      </Routes>
    </Layout>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <VipLimitProvider>
            <div className={styles.App}>
              <AppContent />
            </div>
          </VipLimitProvider>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
